import {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {useParams, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {RootState} from '../../../redux/store.type';
import setGlobalState from '../../../redux/setGlobalState';
import {getDateFormat, getStartOfDay, tsToDate} from '../../../library/dates';
import {TTimetableItem} from '../../../redux/timetable/timetable.type';
import {getLectureTime} from './useTimeGroups';

const ONE_DAY_S = 86_400;

const formatDayLabel = (timestamp: number, lang: string, dayPrefix: string) => {
    const dayName = tsToDate(timestamp, lang, null, 'dddd');
    return `${dayPrefix} ${dayName} - ${getDateFormat(timestamp, 'D.M.YY')}`;
};

export interface TConferenceDay {
    label: string;
    timestamp: number;
    count: number;
}

export function useTimetableDays({controlled = false}: {controlled?: boolean} = {}) {
    const {i18n, t} = useTranslation('timetable');
    const {subPage: dayParam} = useParams();
    const navigate = useNavigate();
    const eid = useSelector((state: RootState) => state.event.eid);
    const timeStart = useSelector((state: RootState) => state.event.timeStart);
    const timeEnd = useSelector((state: RootState) => state.event.timeEnd);
    const lecturesByDay = useSelector((state: RootState) => state.timetable.lectures);
    const slots = useSelector((state: RootState) => state.timetable.slots);
    const getLecturesXHR = useSelector((state: RootState) => state.timetable.getLecturesXHR);
    const reduxDayIndex = useSelector((state: RootState) => state.timetable.currentDayIndex);
    const dayPrefix = t('day');

    const daySkeleton = useMemo(() => {
        if(!timeStart || !timeEnd) return [];
        const days: {label: string; timestamp: number}[] = [];
        let dayTs = getStartOfDay(timeStart);
        const lastDay = getStartOfDay(timeEnd);
        while(dayTs <= lastDay) {
            days.push({
                label: formatDayLabel(dayTs, i18n.language, dayPrefix),
                timestamp: getStartOfDay(dayTs),
            });
            dayTs += ONE_DAY_S;
        }
        return days;
    }, [timeStart, timeEnd, i18n.language, dayPrefix]);

    const conferenceDays = useMemo<TConferenceDay[]>(() => daySkeleton.map(day => ({
        ...day,
        // Count only lectures that actually fall on this day — the bucket can hold
        // entries fetched/keyed under the wrong day, which would inflate the badge.
        // Use the same day-match as the list (useTimeGroups) so they never disagree.
        count: (lecturesByDay[day.timestamp] ?? []).filter(
            (l: TTimetableItem) => !l.isBreak && getStartOfDay(getLectureTime(l, slots)) === day.timestamp,
        ).length,
    })), [daySkeleton, lecturesByDay, slots]);

    // Items whose slot day is no longer inside the conference range. Server is
    // not mutated — re-expanding the dates puts items back on their day.
    // Breaks are excluded: they should simply disappear from the no-date tab.
    const noDateItems = useMemo(() => {
        const conferenceKeys = new Set(conferenceDays.map(d => d.timestamp));
        return Object.values(lecturesByDay)
            .flat()
            .filter(l => !l.isBreak && !conferenceKeys.has(getStartOfDay(getLectureTime(l, slots))));
    }, [lecturesByDay, conferenceDays, slots]);

    // The URL is the single source of truth for the selected day: the segment is
    const currentDayIndex = useMemo(() => {
        if(controlled) return Math.max(-1, Math.min(conferenceDays.length - 1, reduxDayIndex));
        if(dayParam === '0') return -1;     // no-date tab
        if(!dayParam) return 0;             // /timetable -> first day
        const idx = conferenceDays.findIndex(d => d.timestamp === getStartOfDay(Number(dayParam)));
        return idx >= 0 ? idx : 0;          // unknown segment -> first day
    }, [controlled, reduxDayIndex, dayParam, conferenceDays]);

    const dayToSegment = (i: number): string => String(conferenceDays[i]?.timestamp ?? 0);
    const goToDay = (i: number) => (controlled
        ? setGlobalState('timetable.currentDayIndex', i)
        : navigate(`/e/${eid}/timetable/${dayToSegment(i)}`));

    const currentDay = conferenceDays[currentDayIndex];
    const isNoDate = currentDayIndex === -1;
    const noDateCount = noDateItems.filter(l => !l.isBreak).length;

    return {
        conferenceDays,
        isLoadingLectures: !getLecturesXHR || getLecturesXHR === 'LOADING',
        currentDay,
        currentDayIndex,
        noDateItems,
        noDateCount,
        currentDateLabel: isNoDate ? t('noDateLectures') : (currentDay?.label || ''),
        onPrevDay: () => goToDay(Math.max(0, currentDayIndex - 1)),
        onNextDay: () => goToDay(isNoDate ? 0 : Math.min(conferenceDays.length - 1, currentDayIndex + 1)),
        onDaySelect: (i: number) => goToDay(Math.max(-1, Math.min(conferenceDays.length - 1, i))),
        disablePrev: currentDayIndex <= 0,
        disableNext: !isNoDate && currentDayIndex === conferenceDays.length - 1,
    };
}
